import AppError from '@/components/AppError';
import { TappFormField } from '@/components/ui/TappFormField';
import { TappInput } from '@/components/ui/TappInput';
import { TappLabel } from '@/components/ui/TappLabel';
import { TappPageHeader } from '@/components/ui/TappPageHeader';
import { TappSelect } from '@/components/ui/TappSelect';
import { Button } from '@/components/ui/button';
import { cn } from '@/components/ui/utils';
import { UserStatus, Driver } from '@/lib/models/user';
import { Company } from '@/lib/models/company';
import { ApiUtils } from '@/lib/utils/api';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';

const DriverCreatePage = () => {
  const { t } = useTranslation();

  return (
    <>
      <TappPageHeader title={t('addDriver')} defaultBack backLink="/admin/drivers" />
      <CreateForm />
    </>
  );
};

const CreateForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const driverSchema = z.object({
    firstName: z.string().min(1, t('firstName') + ' ' + t('isRequired')),
    lastName: z.string().min(1, t('lastName') + ' ' + t('isRequired')),
    phoneNumber: z.string().min(1, t('phone') + ' ' + t('isRequired')),
    carModel: z.string().min(1, t('car') + ' ' + t('isRequired')),
    licensePlate: z.string().min(1, t('licensePlate') + ' ' + t('isRequired')),
    badgeNumber: z
      .string()
      .min(2, t('badgeNumber') + ' ' + t('isRequired'))
      .max(50, t('badgeNumber') + ' este prea lung'),
    status: z.nativeEnum(UserStatus),
    companyId: z.string().min(1, t('company') + ' ' + t('isRequired')),
    companyName: z.string(),
  });

  const driverStatusOptions = [
    { label: t('active'), value: UserStatus.active },
    { label: t('blocked'), value: UserStatus.blocked },
  ];

  const { data: companies = [], isLoading: isLoadingCompanies } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await ApiUtils.get<Company[]>('company');
      return Array.isArray(response) ? response : [];
    },
  });

  const companyOptions = companies.map((company) => ({
    label: company.name,
    value: company._id,
  }));

  const {
    handleSubmit,
    control,
    setValue,
    formState: { isSubmitting, isSubmitted },
  } = useForm<Driver>({
    resolver: zodResolver(driverSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      phoneNumber: '',
      carModel: '',
      licensePlate: '',
      badgeNumber: '',
      status: UserStatus.active,
      companyId: '',
      companyName: '',
    },
  });

  const mutation = useMutation({
    mutationFn: (data: Driver) => ApiUtils.post<Driver>('users/driver', data),
    onSuccess: () => {
      navigate('/admin/drivers');
    },
  });

  const onSubmit = (data: Driver) => {
    mutation.mutate(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={cn('flex w-full max-w-md flex-1 flex-col items-start gap-2')}>
      <TappFormField>
        <TappLabel htmlFor="firstName">{t('firstName')}</TappLabel>
        <Controller
          name="firstName"
          control={control}
          render={({ field, fieldState }) => (
            <TappInput className="w-full" placeholder={'Ion'} {...field} {...fieldState} />
          )}
        />
      </TappFormField>

      <TappFormField>
        <TappLabel htmlFor="lastName">{t('lastName')}</TappLabel>
        <Controller
          name="lastName"
          control={control}
          render={({ field, fieldState }) => (
            <TappInput className="w-full" placeholder={'Popescu'} {...field} {...fieldState} />
          )}
        />
      </TappFormField>

      <TappFormField>
        <TappLabel htmlFor="phoneNumber">{t('phone')}</TappLabel>
        <Controller
          name="phoneNumber"
          control={control}
          render={({ field, fieldState }) => (
            <TappInput className="w-full" placeholder={'+40712345678'} {...field} {...fieldState} />
          )}
        />
      </TappFormField>

      <TappFormField>
        <TappLabel htmlFor="carModel">{t('car')}</TappLabel>
        <Controller
          name="carModel"
          control={control}
          render={({ field, fieldState }) => (
            <TappInput className="w-full" placeholder={'Dacia Logan'} {...field} {...fieldState} />
          )}
        />
      </TappFormField>

      <TappFormField>
        <TappLabel htmlFor="licensePlate">{t('licensePlate')}</TappLabel>
        <Controller
          name="licensePlate"
          control={control}
          render={({ field, fieldState }) => (
            <TappInput className="w-full" placeholder={'CJ-12-ABC'} {...field} {...fieldState} />
          )}
        />
      </TappFormField>

      <TappFormField>
        <TappLabel htmlFor="badgeNumber">{t('badgeNumber')}</TappLabel>
        <Controller
          name="badgeNumber"
          control={control}
          render={({ field, fieldState }) => (
            <TappInput className="w-full" placeholder={'D-12345'} {...field} {...fieldState} />
          )}
        />
      </TappFormField>

      <TappFormField>
        <TappLabel htmlFor="companyId">{t('company')}</TappLabel>
        <Controller
          name="companyId"
          control={control}
          render={({ field: { value, onChange, name, onBlur }, fieldState }) => (
            <TappSelect
              className="w-full"
              options={companyOptions}
              value={companyOptions.find((opt) => opt.value === value)}
              onChange={(val) => {
                onChange(val?.value);
                setValue('companyName', val?.label || '');
              }}
              name={name}
              onBlur={onBlur}
              isLoading={isLoadingCompanies}
              isSubmitted={isSubmitted}
              {...fieldState}
            />
          )}
        />
      </TappFormField>

      <TappFormField>
        <TappLabel htmlFor="status">{t('status')}</TappLabel>
        <Controller
          name="status"
          control={control}
          render={({ field: { value, onChange, name, onBlur }, fieldState }) => (
            <TappSelect
              className="w-full"
              options={driverStatusOptions}
              value={driverStatusOptions.find((opt) => opt.value === value)}
              onChange={(val) => onChange(val?.value)}
              name={name}
              onBlur={onBlur}
              {...fieldState}
            />
          )}
        />
      </TappFormField>

      {mutation.isError && <AppError error={mutation.error} className="mt-4" />}

      <Button className="mt-2" type="submit" disabled={isSubmitting || mutation.isPending}>
        {mutation.isPending ? t('saving') : t('save')}
      </Button>
    </form>
  );
};

export default DriverCreatePage;
